import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, OnDestroy } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ValidationErrors,
} from '@angular/forms';
import { CurrencySign, initialDeliveryServiceDynamicFieldsVariables } from '@pdqdev/pdq-shared/dist/entities';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { ShippingMethod, Shippings } from '@pages/checkout-app/types';
import {
  ALLOWED_DELIVERY_METHOD_TYPES,
  DisplayRatesAs,
  InternationalDescriptionMap,
} from '@pages/checkout-app/configs';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { getCurrencyUnitsOptions } from '@utils/currency-utils';
import { ICheckoutVariantABTest } from '@pdqdev/pdq-shared/dist/interfaces';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { Observable, Subject } from 'rxjs';
import { ECOMMERCES } from '@services/shops/shop.model';
import { currencyStrToSign } from '@utils/string-utils';
import { takeUntil } from 'rxjs/operators';
import { rateHasAnyDiscount } from '@pages/checkout-app/utils/string.utils';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { stripHtmlTags } from '@utils/string-utils';

interface ShippingRateDiscount {
  hasDiscount: boolean;
  discountType: string;
  discountAmount: number;
  minPriceRange: number;
  maxPriceRange?: number | null;
}

interface ShippingRateDiscountMetafieldValue {
  shippingRateId: string;
  shippingRateTitle: string;
  shippingRateDescription: string;
  shippingRateDiscounts: ShippingRateDiscount[];
}
@Component({
  selector: 'general-settings',
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss'],
  standalone: false,
})
export class GeneralSettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() deliveryType: string;
  @Input() shippingMethodCategory: string;
  @Input() showFormErrors: boolean;
  @Input() shopCurrency: CurrencySign;
  @Input() shop: Shop;
  @Input() loadedValues: Shippings;
  @Input() runningABTest: ICheckoutVariantABTest | null;
  @Input() requiredFormControlsMap: {};

  @Output() deliveryMethodSectionsLoaded = new EventEmitter<DropdownOption[]>();

  readonly DisplayRatesAs = [...DisplayRatesAs];
  readonly ShippingMethod = ShippingMethod;
  readonly currencyUnitsOptions = getCurrencyUnitsOptions();
  deliveryMethodSectionOptions: DropdownOption[] = [];
  selectedDeliveryMethodSectionOption: DropdownOption | null = null;
  shippingMethodCurrency$: Observable<any>;
  onSetSelectedCarrier$: Observable<any>;
  additionalPriceRanges = new UntypedFormArray([]);
  changeDeliveryTypeSupported = false;
  deliveryTypeOptions = [];
  shopCurrencySign;
  displayTaxesAndDutiesBreakdownCheckbox = false;
  private destroy$ = new Subject<void>();
  private allShippingMethods: Shippings[] = [];
  private checkoutGroupIdWithLoadedSections: number | null = null;

  get showDeliveryPromiseHelper(): boolean {
    const nameValue = this.formGroup?.get('name')?.value || '';
    return nameValue.includes('{delivery_promise}');
  }

  get isNotesRequired(): boolean {
    return !!this.duplicateNameAndDescriptionForDiscountedRatesValidator();
  }

  get notesInvalidMsg(): string | null {
    if (this.runningABTest) {
      return 'Disabled due to running A/B test';
    }

    const notesControl = this.formGroup.get('notes');
    if (notesControl?.hasError('required')) {
      return 'A rate with this name already exists, add a note to make it unique';
    }

    if (notesControl?.hasError('duplicateNameNotes')) {
      return notesControl.errors?.['duplicateNameNotes']?.message;
    }

    return null;
  }

  get notesLabel(): string {
    const hasDuplicateError = this.formGroup.get('name')?.hasError('duplicateNameNotes');
    if (hasDuplicateError) {
      return 'Note on delivery method (required for discounted rates)';
    }
    return 'Note on delivery method (optional)';
  }

  deliveryMethodConfig = {
    items: initialDeliveryServiceDynamicFieldsVariables,
    triggerChar: '{',
    labelKey: 'description',
    descriptionKey: 'name',
    filterByKey: 'searchTerm',
    removeTriggerCharOnSelect: true,
    variant: 'categorizedOptions',
  };

  // Discount type options
  discountTypeOptions = [
    { value: 'fixed', label: 'Fixed Amount' },
    { value: 'percentage', label: 'Percentage' },
  ];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private checkoutService: CheckoutService,
    private store: Store<fromCheckoutReducer.State>,
  ) {}

  ngOnInit() {
    this.shippingMethodCurrency$ = this.checkoutService.shippingMethodCurrency$;
    this.onSetSelectedCarrier$ = this.checkoutService.onSetSelectedCarrier$;
    this.shopCurrencySign = currencyStrToSign(this.shopCurrency);

    this.store
      .select(fromCheckoutReducer.selectShippingMethods)
      .pipe(takeUntil(this.destroy$))
      .subscribe((shippingMethods) => {
        this.allShippingMethods = shippingMethods || [];
      });

    this.setupMainRateValidation();
    this.loadDeliveryMethodSectionOptions();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupMainRateValidation() {
    const mainRateControl = this.formGroup.get('rate');
    const minOrderControl = this.formGroup.get('minOrderPriceRange');
    const maxOrderControl = this.formGroup.get('maxOrderPriceRange');
    const discountAmountControl = this.formGroup.get('discountAmount');

    // Skip validation setup for live rate shipping methods
    if (mainRateControl) {
      if (this.isLiveRateShippingMethod || this.isPickUpShippingMethod) {
        mainRateControl.clearValidators();
      } else {
        mainRateControl.setValidators([Validators.required, Validators.min(0)]);
      }
      mainRateControl.updateValueAndValidity();
    }

    if (discountAmountControl) {
      if (this.isLiveRateShippingMethod || this.isLiveRateETAFixedRate) {
        discountAmountControl.clearValidators();
      } else {
        discountAmountControl.setValidators([Validators.min(0)]);
      }
      discountAmountControl.updateValueAndValidity();
    }

    if (minOrderControl) {
      minOrderControl.setValidators([Validators.min(0)]);
      minOrderControl.updateValueAndValidity();
    }

    if (maxOrderControl) {
      maxOrderControl.updateValueAndValidity();
    }

    this.setupRichTextFieldsProcessing();
  }

  private setupRichTextFieldsProcessing(): void {
    const nameControl = this.formGroup?.get('name');
    const notesControl = this.formGroup?.get('notes');

    if (nameControl) {
      this.setupHtmlStrippingForControl(nameControl);
    }

    if (notesControl) {
      this.setupHtmlStrippingForControl(notesControl);
    }
  }

  private setupHtmlStrippingForControl(control: any): void {
    control.valueChanges.subscribe((value) => {
      if (value && value.includes('<p>')) {
        const plainText = stripHtmlTags(value);
        if (plainText !== value) {
          control.setValue(plainText, { emitEvent: false });
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.formGroup?.currentValue?.value?.shippingMethodCategory?.includes('international')) {
      const internationalTaxPaymentValue = changes?.formGroup?.currentValue?.value?.international?.taxPaymentType;
      this.setInternationalDescription(internationalTaxPaymentValue);
    }
    this.additionalPriceRanges = this.formGroup.get('additionalPriceRanges') as UntypedFormArray;
    this.handleChangeDeliveryTypeSupport();

    if (changes?.formGroup) {
      this.setupRichTextFieldsProcessing();
      this.setupMainRateValidation();
      this.setupDiscountValidationWatchers();
    }

    if (changes?.loadedValues) {
      this.loadDeliveryMethodSectionOptions();
    }
  }

  handleTaxPaymentTypeChange(event) {
    const internationalForm = this.formGroup.controls.international as UntypedFormGroup;
    internationalForm.controls.taxPaymentType.setValue(event);
    this.setInternationalDescription(event, true);
  }

  setInternationalDescription(value: 'DDU' | 'DDP', overrideNotes: boolean = false) {
    if (overrideNotes) {
      this.formGroup.controls['notes'].setValue(InternationalDescriptionMap[value]);
    } else if (value && !this.formGroup.value.notes && !this.loadedValues) {
      this.formGroup.controls['notes'].setValue(InternationalDescriptionMap[value]);
    }
  }

  handleChangeDeliveryTypeSupport() {
    const shopPlatform = this.shop?.platform as ECOMMERCES;
    this.changeDeliveryTypeSupported = shopPlatform == 'custom';
    if (this.changeDeliveryTypeSupported) {
      this.deliveryTypeOptions = ALLOWED_DELIVERY_METHOD_TYPES.filter((type) =>
        type.supportedPlatforms.includes(shopPlatform),
      ).map((deliveryType) => ({
        value: deliveryType.value,
        label: (deliveryType.value.charAt(0).toUpperCase() + deliveryType.value.slice(1).toLowerCase()).replace(
          /_/g,
          ' ',
        ),
      }));
    }
  }

  get isDomesticShippingMethod() {
    return [ShippingMethod.DOMESTIC].includes(this.shippingMethodCategory as ShippingMethod);
  }

  get isPickUpShippingMethod() {
    return this.shippingMethodCategory === ShippingMethod.STORE_PICK_UP;
  }

  get isLiveRateShippingMethod() {
    return [ShippingMethod.INTERNATIONAL_LIVE_RATE, ShippingMethod.DOMESTIC_LIVE_RATE].includes(
      this.shippingMethodCategory as ShippingMethod,
    );
  }

  get isUsingRateCard() {
    return !!this.loadedValues?.rateCardStrategy;
  }

  get isInternationalShippingMethod() {
    return this.shippingMethodCategory.includes('international');
  }

  get isLiveRateETAFixedRate() {
    return [ShippingMethod.FIXED_LIVE_RATE, ShippingMethod.INTERNATIONAL_FIXED_LIVE_RATE].includes(
      this.shippingMethodCategory as ShippingMethod,
    );
  }

  get existingShippingHasDiscount(): boolean {
    return !!this.loadedValues?.hasDiscount;
  }

  get globaleAcceptedProposedNames(): string[] {
    return (this.shop?.metadata as { globaleAcceptedProposedNames?: string[] })?.globaleAcceptedProposedNames ?? [];
  }

  get isProposedNameUnmapped(): boolean {
    if (!this.checkoutService.isGlobaleMode || !this.globaleAcceptedProposedNames.length) {
      return false;
    }
    const proposedName = stripHtmlTags(this.formGroup?.get('name')?.value || '').trim();
    return !!proposedName && !this.globaleAcceptedProposedNames.includes(proposedName);
  }

  onMethodCurrencyChange(event: any) {
    this.checkoutService.shippingMethodCurrency = event.value;
  }

  addAdditionalPriceRange(): void {
    this.additionalPriceRanges.push(this.initFormGroup());
  }

  removeAdditionalPriceRange(index: number): void {
    this.additionalPriceRanges.removeAt(index);
  }

  removeMainRate(): void {
    this.formGroup.get('rate')?.setValue(null);
    this.formGroup.get('minOrderPriceRange')?.setValue(0);
    this.formGroup.get('maxOrderPriceRange')?.setValue(null);
    this.formGroup.get('hasDiscount')?.setValue(false);
    this.formGroup.get('discountType')?.setValue('fixed');
    this.formGroup.get('discountAmount')?.setValue(null);
  }

  canRemoveMainRate(): boolean {
    return this.additionalPriceRanges.length > 0;
  }

  findDefaultDeliveryType() {
    return this.deliveryTypeOptions.find((option) => option.value == this.deliveryType);
  }

  handleChangeDeliveryType(option: DropdownOption) {
    const deliveryTypeControl = this.formGroup.get('deliveryType');
    deliveryTypeControl.patchValue(option.value);
  }

  isAllRequiredFieldsSet() {
    let answer = this.formGroup.get('name').valid && this.hasAtLeastOneRate();
    if (this.shop?.platform == 'custom') {
      answer = answer && this.formGroup.get('deliveryType')?.valid;
    }
    return answer;
  }

  hasAtLeastOneRate(): boolean {
    // For live rate shipping methods, rates are not required as they come from carriers
    if (this.isLiveRateShippingMethod || this.isPickUpShippingMethod) {
      return true;
    }

    // Check if main rate has a valid value
    const mainRate = this.formGroup.get('rate')?.value;
    const hasMainRate = this.isRateValid(mainRate);

    if (hasMainRate) {
      return true;
    }

    // Check additional rates - must have at least one with a valid rate value
    const additionalRates = this.additionalPriceRanges.controls;
    if (additionalRates.length === 0) {
      return false; // No additional rates and no main rate
    }

    return additionalRates.some((control) => {
      const rate = control.get('rate')?.value;
      return this.isRateValid(rate);
    });
  }

  isRateValid(rate: any): boolean {
    return rate !== null && rate !== undefined && rate !== '' && rate >= 0;
  }

  initFormGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      minOrderPriceRange: new UntypedFormControl(0, [Validators.min(0)]),
      maxOrderPriceRange: new UntypedFormControl(null),
      rate: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
      hasDiscount: new UntypedFormControl(false),
      discountType: new UntypedFormControl('fixed'),
      discountAmount: new UntypedFormControl(null, [Validators.min(0)]),
    });
  }

  get isNameRequired(): boolean {
    return this.requiredFormControlsMap[this.shippingMethodCategory].some((x: any) => x.name === 'name');
  }

  onDiscountTypeChange(event: any, isMainRate: boolean = false, index?: number): void {
    if (isMainRate) {
      const discountTypeControl = this.formGroup.get('discountType');
      discountTypeControl?.setValue(event.value);
    } else if (index !== undefined) {
      const discountTypeControl = this.additionalPriceRanges.at(index).get('discountType');
      discountTypeControl?.setValue(event.value);
    }
  }

  getDiscountSymbol(discountType: string): string {
    if (discountType === 'percentage') {
      return '%';
    }
    return this.shopCurrencySign;
  }

  getDiscountPlaceholder(discountType: string): string {
    if (discountType === 'percentage') {
      return 'Discount percentage (e.g., 10 for 10%)';
    }
    return 'Discount amount';
  }

  private setupDiscountValidationWatchers(): void {
    if (this.isLiveRateShippingMethod || this.isLiveRateETAFixedRate) {
      return;
    }

    const mainHasDiscountControl = this.formGroup.get('hasDiscount');
    const nameControl = this.formGroup.get('name');
    const notesControl = this.formGroup.get('notes');

    // Watch for discount checkbox changes
    if (mainHasDiscountControl) {
      mainHasDiscountControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updateValidation();
      });
    }

    // Watch for additional price ranges discount changes
    this.additionalPriceRanges.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateValidation();
    });

    // Watch for name and notes changes to re-validate duplicates
    if (nameControl) {
      nameControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updateValidation();
      });
    }

    if (notesControl) {
      notesControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updateValidation();
      });
    }

    this.updateValidation();
  }

  private updateValidation(): void {
    const notesControl = this.formGroup.get('notes');
    const nameControl = this.formGroup.get('name');

    if (!notesControl || !nameControl) return;

    // Set validators for notes field
    const notesValidators = [this.duplicateNameAndDescriptionForDiscountedRatesValidator.bind(this)];

    const duplicateError = this.duplicateNameAndDescriptionForDiscountedRatesValidator();
    if (duplicateError) {
      notesValidators.unshift(Validators.required);
      notesControl.markAsTouched();
    }
    notesControl.setValidators(notesValidators);

    // Set validators for name field
    nameControl.setValidators([
      Validators.required,
      this.duplicateNameAndDescriptionForDiscountedRatesValidator.bind(this),
    ]);

    // Update validation without emitting events
    nameControl.updateValueAndValidity({ emitEvent: false });
    notesControl.updateValueAndValidity({ emitEvent: false });
  }

  private duplicateNameAndDescriptionForDiscountedRatesValidator(): ValidationErrors | null {
    if (!this.formGroup) return null;

    const currentName = stripHtmlTags(this.formGroup.get('name')?.value || '')
      .trim()
      .toLowerCase();
    const currentNotes = stripHtmlTags(this.formGroup.get('notes')?.value || '')
      .trim()
      .toLowerCase();

    if (!currentName) return null;

    const currentCombination = currentName + currentNotes;
    const MainRateHasDiscount = this.formGroup.get('hasDiscount')?.value;
    const AdditionalPriceRanges = this.formGroup.get('additionalPriceRanges')?.value || [];
    const AdditionalPriceRangesHasDiscount = AdditionalPriceRanges.some((item: any) => item.hasDiscount === true);

    const currentShippingMethodHasDiscount = MainRateHasDiscount || AdditionalPriceRangesHasDiscount;

    const hasDuplicateWithDiscount = this.allShippingMethods?.some((shippingMethod) => {
      if (
        this.loadedValues?.shippingMethodId &&
        shippingMethod.shippingMethodId === this.loadedValues.shippingMethodId
      ) {
        return false;
      }

      const shippingName = stripHtmlTags(shippingMethod.name || '')
        .trim()
        .toLowerCase();
      const shippingNotes = stripHtmlTags(shippingMethod.notes || '')
        .trim()
        .toLowerCase();

      const existingCombination = shippingName + shippingNotes;

      // You cannot have two shipping methods with the same name+description if either has a discount
      if (existingCombination === currentCombination) {
        const existingHasDiscount = rateHasAnyDiscount(shippingMethod);
        return currentShippingMethodHasDiscount || existingHasDiscount;
      }

      return false;
    });

    if (hasDuplicateWithDiscount) {
      return {
        duplicateNameNotes: {
          message: 'A shipping method with this Name + Description combination already exists.',
        },
      };
    }

    return null;
  }

  private loadDeliveryMethodSectionOptions() {
    const checkoutGroupId = Number(
      this.loadedValues?.shippingMethodGroupId ?? new URLSearchParams(window.location.search).get('collectionId'),
    );
    if (!checkoutGroupId || checkoutGroupId === this.checkoutGroupIdWithLoadedSections) {
      return;
    }
    this.checkoutGroupIdWithLoadedSections = checkoutGroupId;
    this.checkoutService
      .loadDeliveryMethodSectionsOfCheckoutGroup(checkoutGroupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((deliveryMethodSections) => {
        this.deliveryMethodSectionOptions = [
          { label: 'No section', value: '' },
          ...deliveryMethodSections.map((section) => ({ label: section.name, value: section.id })),
        ];
        this.deliveryMethodSectionsLoaded.emit(this.deliveryMethodSectionOptions);
        const currentSectionId = this.formGroup.get('deliveryMethodSectionId')?.value ?? '';
        this.selectedDeliveryMethodSectionOption =
          this.deliveryMethodSectionOptions.find((option) => option.value === currentSectionId) ??
          this.deliveryMethodSectionOptions[0];
      });
  }
}
